import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TevelCyberLogo from "../../../assets/img/TevelCyberFinalBlueLogo.png";
import LoginImage1 from "../../../assets/img/LoginIllustraion.png";
import CyberBay from "../../../assets/img/CyberBayFinalBlueLogo.png";
import AuthContext from "../../../auth/AuthContext";
import LoginService from "../../../services/LoginService";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import AlertService from "../../../services/AlertService";
import LockIcon from "../../../assets/icons/LockIcon.svg";
import InfoIcon from "../../../assets/icons/infoIcon.svg";

const Reset = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const submitControllerRef = useRef(null);
  const [errors, setErrors] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [strengthColor, setStrengthColor] = useState("");

  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const newPasswordRef = useRef();
  const confirmPasswordRef = useRef();

  // Validate reset password status
  const validateVerified = useCallback(() => {
    if (authContext.resetPassword === false) {
      navigate("/");
    }
  }, [authContext, navigate]);

  useEffect(() => {
    validateVerified();
  }, [validateVerified]);

  const validateForm = () => {
    let isValid = true;
    const newErrors = { newPassword: "", confirmPassword: "" };
    const newPassword = newPasswordRef.current.value;
    const confirmPassword = confirmPasswordRef.current.value;

    // Strong password regex
    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;

    if (!newPassword) {
      newErrors.newPassword = "Password is required";
      isValid = false;
    } else if (!strongPasswordRegex.test(newPassword)) {
      newErrors.newPassword =
        "Password must be 8+ chars, include uppercase, lowercase, number & special char";
      isValid = false;
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };
  const handlePasswordChange = () => {
    const password = newPasswordRef.current.value;
    let strength = 0;

    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/\d/.test(password)) strength += 1;
    if (/[\W_]/.test(password)) strength += 1;

    if (strength <= 2) {
      setPasswordStrength("Weak");
      setStrengthColor("red");
    } else if (strength === 3 || strength === 4) {
      setPasswordStrength("Medium");
      setStrengthColor("orange");
    } else if (strength === 5) {
      setPasswordStrength("Strong");
      setStrengthColor("green");
    } else {
      setPasswordStrength("");
      setStrengthColor("");
    }
  };


const onSubmitHandler = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const payload = {
      key: authContext.userId.toString(),
      value: newPasswordRef.current.value,
    };

    const result = await LoginService.ResetPassword(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    authContext.setResetPasswordCompleted(true);

    if (result.isSuccess) {
      if (
        authContext.mfaSetupEnabled === true &&
        authContext.mfaSetupCompleted === false
      ) {
        navigate("/qrcode");
      } else if (authContext.isAnswered === false) {
        navigate("/questions");
      } else {
        navigate("/logout");
      }
    } else {
      AlertService.error(result.errorMessage);
    }
  } catch (error) {
    if (error.name === "AbortError") return;
    console.error("Reset error:", error);
    AlertService.error("Failed to reset password");
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

  return (
    <div className="container-fluid min-vh-100 d-flex flex-column flex-xl-row p-0">
      {/* Left Section */}
      {/* Desktop Logo */}
      <div className="col-xl-6 d-flex flex-column justify-content-center align-items-center p-3 p-lg-5 min-vh-100  ">
        <div className="d-none d-xl-block">
          <img
            src={TevelCyberLogo}
            alt="Company Logo"
            style={{ maxWidth: "217px", marginRight: "220px" }}
            className="img-fluid"
          />
        </div>
        {/* Full Height Flex to Center Logos + Form */}
        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1 w-100">
          <div className="d-flex d-xl-none justify-content-between align-items-center gap-5 mt-3 mb-4">
            <img
              src={TevelCyberLogo}
              alt="Tevel Cyber Logo"
              className="img-fluid"
              style={{ width: "140px" }}
            />
            <img
              src={CyberBay}
              alt="Cyber Bay Logo"
              className="img-fluid"
              style={{ width: "120px" }}
            />
          </div>
          {/* Form Block */}
          <div className="w-100 mb-xl-4" style={{ maxWidth: "439px" }}>
            <div className="d-flex justify-content-center align-items-center mb-4">
              <h4 className="fw-bold m-0">Create Password</h4>
              <img
                src={InfoIcon}
                alt="info"
                style={{
                  width: "18px",
                  height: "16px",
                  cursor: "pointer",
                  marginTop: "-16px",
                }}
                title="Your password should contain at least 8 characters including uppercase, lowercase, number, and symbol."
              />
            </div>

            <form onSubmit={onSubmitHandler}>
              {/* New Password Field */}
              <div className="mb-4 position-relative">
                <div className="input-group">
                  <span className="input-group-text bg-transparent border-end-0">
                    <img src={LockIcon} alt="lockIcon" />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="newPassword"
                    className="form-control border-start-0 border-end-0 custom-input"
                    placeholder="New Password"
                    maxLength={30}
                    style={{ paddingLeft: "5px" }}
                    ref={newPasswordRef}
                    onChange={handlePasswordChange}
                  />
                  <span
                    className="input-group-text bg-transparent border-start-0"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ cursor: "pointer", color: "#888" }}
                  >
                    {showPassword ? (
                      <FaEyeSlash className="fs-5" />
                    ) : (
                      <FaEye className="fs-5" />
                    )}
                  </span>
                </div>
                {newPasswordRef.current?.value && (
                  <div className="mt-0">
                    <div
                      style={{
                        height: "8px",
                        borderRadius: "4px",
                        backgroundColor: "#e0e0e0",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width:
                            passwordStrength === "Weak"
                              ? "33%"
                              : passwordStrength === "Medium"
                              ? "66%"
                              : passwordStrength === "Strong"
                              ? "100%"
                              : "0%",
                          backgroundColor: strengthColor,
                          borderRadius: "4px",
                          transition: "width 0.3s ease-in-out",
                        }}
                      ></div>
                    </div>
                    <div
                      className="text-end"
                      style={{ fontSize: "0.8rem", color: strengthColor }}
                    >
                      {passwordStrength}
                    </div>
                  </div>
                )}

                {errors.newPassword && (
                  <p className="text-danger fw-normal error-bottom">
                    {errors.newPassword}
                  </p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="mb-4 position-relative">
                <div className="input-group">
                  <span className="input-group-text bg-transparent border-end-0">
                    <img src={LockIcon} alt="lockIcon" />
                  </span>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    className="form-control border-start-0 border-end-0 custom-input"
                    placeholder="Confirm Password"
                    maxLength={30}
                    style={{ paddingLeft: "5px" }}
                    ref={confirmPasswordRef}
                  />
                  <span
                    className="input-group-text bg-transparent border-start-0"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{ cursor: "pointer", color: "#888" }}
                  >
                    {showConfirmPassword ? (
                      <FaEyeSlash className="fs-5" />
                    ) : (
                      <FaEye className="fs-5" />
                    )}
                  </span>
                </div>
                {errors.confirmPassword && (
                  <p className="text-danger fw-normal error-bottom">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="btn w-100 mb-3 btn-primary custom-input mt-2"
                disabled={loading}
              >
                {loading ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : null}
                {loading ? "Submitting..." : "Submit"}
              </button>
            </form>
          </div>
        </div>
        <footer className="mt-auto text-center w-100">
          <p className="text-muted small mb-0">
            © 2026 Tevel Cyber Corps Pvt. Ltd. All rights reserved - Version 2.2.0
          </p>
        </footer>
      </div>

      {/* Right Section */}
      <div className="col-xl-6 d-none d-xl-flex flex-column login-bg  min-vh-100 align-items-center justify-content-center  ">
        <div className="p-5 mt-2">
          <img
            src={CyberBay}
            alt="CYBER BAY"
            className="img-fluid mb-4"
            width="130px"
          />
        </div>
        <div className='"d-flex flex-column justify-content-center align-items-center flex-grow-1 w-100 p-xl-5'>
          <img
            src={LoginImage1}
            alt="Decorative"
            className="img-fluid loginImg"
            style={{
              maxWidth: "690px",
              maxHeight: "484px",
              marginLeft: "130px",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Reset;
